import React, { useState } from 'react';
import { AppShell } from '@mantine/core';
import { useNavigation } from '../contexts/NavigationContext';
import { useTodoFilter } from '../contexts/TodoFilterContext';
import { useBackgroundImage } from '../hooks/useBackgroundImage';
import { AppHeader } from './AppHeader';
import { NavigationBar } from './NavigationBar';
import { FloatingAddButton } from './FloatingAddButton';
import { LeanAddAndEditTaskDialog } from './LeanAddAndEditTaskDialog';

interface AppLayoutProps {
  children: React.ReactNode;
  onShowNotifications?: () => void;
  onShowFeedback?: () => void;
}

export const AppLayout: React.FC<AppLayoutProps> = ({ children, onShowNotifications, onShowFeedback }) => {
  const { isNavbarOpen, isMobile, closeNavbar, currentMode } = useNavigation();
  const { currentListId } = useTodoFilter();
  const { backgroundClass } = useBackgroundImage();
  const [isAddTaskDialogOpen, setIsAddTaskDialogOpen] = useState(false);

  // Only show FAB in Todos mode
  const showFAB = currentMode === 'todos';

  // NotificationPanel positioned below this header (top: 60px/70px) - no z-index conflicts
  return (
    <AppShell
      header={{ height: isMobile ? 60 : 70, offset: true }}
      navbar={{
        width: 280,
        breakpoint: 'md', // md is 992px in Mantine - good balance for mobile/desktop
        collapsed: { mobile: !isNavbarOpen, desktop: false }
      }}
      withBorder={false}
      padding="0"
    >
      <AppShell.Header>
        <AppHeader onShowNotifications={onShowNotifications} onShowFeedback={onShowFeedback} />
      </AppShell.Header>

      <AppShell.Navbar>
        <NavigationBar onShowFeedback={onShowFeedback} />
      </AppShell.Navbar>

      <AppShell.Main
        className={backgroundClass}
        onClick={() => {
          if (isMobile && isNavbarOpen) {
            closeNavbar();
          }
        }}
      >
        {children}
      </AppShell.Main>

      {/* Floating Add Button - only visible in Todos mode, hidden when dialog is open */}
      {showFAB && (
        <FloatingAddButton 
          onClick={() => setIsAddTaskDialogOpen(true)} 
          isVisible={!isAddTaskDialogOpen}
        />
      )}

      {/* Lean Add/Edit Task Dialog - only in Todos mode */}
      {showFAB && (
        <LeanAddAndEditTaskDialog
          opened={isAddTaskDialogOpen}
          onClose={() => setIsAddTaskDialogOpen(false)}
          selectedListId={currentListId || undefined}
        />
      )}
    </AppShell>
  );
};
